import React, { useEffect, useState } from "react";
import styles from "./EmailLogs.module.scss";
import AdminHeader from "../../../Components/AdminHeader/AdminHeader";
import { getEmailLogs } from "../../../Redux/Stats/StatsActions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../Redux/store";
import toast from "react-hot-toast";
import InlineLoader from "../../../Components/InlineLoader/InlineLoader";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridReadyEvent,
  RowClickedEvent,
  ValueGetterParams,
} from "ag-grid-community";
import EmailLogModal from "../../../Components/EmailLogModal/EmailLogModal";
import { DateTime } from "luxon";

export interface EmailLogsType {
  from: string;
  to: string;
  subject: string;
  body: string;
  number?: string;
  awsEmailId: string;
  awsEmailResponse: any;
  createdAt: string;
  token?: string;
  triggerType: string;
}

const columnDefs: ColDef<EmailLogsType>[] = [
  {
    headerName: "Number",
    field: "number",
    flex: 1,
    maxWidth: 200,
    valueGetter: (params: ValueGetterParams<EmailLogsType>) => {
      return params?.data?.number ? params?.data?.number : "N/A";
    },
  },

  {
    headerName: "From",
    field: "from",
    flex: 1,
    maxWidth: 200,
  },
  {
    headerName: "To",
    field: "to",
    flex: 1,
    maxWidth: 200,
  },

  {
    headerName: "Subject",
    field: "subject",
    flex: 1,
  },

  {
    headerName: "Token",
    field: "token",
    flex: 1,
    valueGetter: (params: ValueGetterParams<EmailLogsType>) => {
      return params?.data?.token ? params?.data?.token : "N/A";
    },
  },

  {
    headerName: "Trigger",
    field: "triggerType",
    flex: 1,
  },

  {
    headerName: "AWS Response",
    valueGetter: (params: ValueGetterParams<EmailLogsType>) => {
      const response = params.data?.awsEmailResponse;
      if (!response) return "N/A";

      let parsed;
      try {
        parsed = typeof response === "string" ? JSON.parse(response) : response;
      } catch {
        return "Invalid JSON";
      }

      const type = parsed?.notificationType?.toLowerCase() || "";

      switch (type) {
        case "delivery":
          return "Delivered Successfully";
        case "bounce":
          return "Bounced";
        case "complaint":
          return "Complaint";
        case "failure":
          return "Failed";
        default:
          if (parsed?.delivery?.smtpResponse?.includes("250")) {
            return "Delivered Successfully";
          }
          return "Unknown";
      }
    },
  },
  {
    headerName: "Created At",
    field: "createdAt",
    flex: 1,
    maxWidth: 200,
    minWidth: 150,
    valueGetter: (params) => {
      return params?.data?.createdAt
        ? DateTime.fromISO(params?.data?.createdAt, {
            zone: "America/New_York",
          }).toFormat("MM/dd/yyyy hh:mm a")
        : "";
    },
  },
];

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  suppressPaste: true,
  suppressNavigable: true,
  suppressMovable: true,
  wrapText: true,
  autoHeight: true,
};

function EmailLogs() {
  const dispatch = useDispatch<AppDispatch>();
  const { email_logs_loading, email_logs_data } = useSelector(
    (state: RootState) => state.stats
  );
  const [search, setSearch] = useState("testrrttest@yopmail.com");
  const [rowData, setRowData] = useState<EmailLogsType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [selectedRowData, setSelectedRowData] = useState<EmailLogsType | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const searchHandler = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (search) {
      if (!email_logs_loading) {
        dispatch(getEmailLogs(search));
      }
    } else {
      toast.error("Please enter a search term");
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  };

  const onRowClicked = (event: RowClickedEvent<EmailLogsType>) => {
    if (event?.data) {
      setSelectedRowData(event.data);
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    if (email_logs_data) {
      setRowData(email_logs_data);
    }
  }, [email_logs_data]);

  const refreshHandler = () => {
    if (!email_logs_loading) {
      dispatch(getEmailLogs(search));
    }
  };

  return (
    <React.Fragment>
      <EmailLogModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        selectedRowData={selectedRowData}
      />

      <div className={styles.mainContainer}>
        <AdminHeader />
        <div className={styles.bodyContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Search Logs</h1>
          </div>
          <form className={styles.searchContainer}>
            <div className={styles.leftContainer}>
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  placeholder="Search"
                  className={styles.input}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className={styles.buttonContainer}>
                <button
                  type="submit"
                  className={styles.button}
                  onClick={searchHandler}
                >
                  Search
                </button>
              </div>
            </div>
            <div className={styles.rightContainer}>
              <div className={styles.buttonContainer}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={refreshHandler}
                >
                  Refresh
                </button>
              </div>
            </div>
          </form>
          {email_logs_loading && (
            <div className={styles.loadingContainer}>
              <InlineLoader color="#000000" />
            </div>
          )}

          {Array.isArray(email_logs_data) && email_logs_data.length === 0 && (
            <div className={styles.noLogsContainer}>
              <p className={styles.noLogsText}>No logs found</p>
            </div>
          )}

          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={false}
              animateRows={true}
              domLayout="autoHeight"
              onRowClicked={onRowClicked}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default EmailLogs;
